/**
 * SHELL — daily reminder hooks (the retention nudge).
 *
 * Two delivery paths from one API:
 *   - NATIVE (Capacitor): schedule a repeating LOCAL notification at a chosen
 *     local hour via @capacitor/local-notifications (dynamically imported so the
 *     web bundle never depends on it). Push (remote) registration hook is also
 *     exposed for the Backend to target via FCM/APNs.
 *   - WEB: request Notification permission and (best-effort) show an immediate
 *     confirmation; true scheduled web reminders require the push service which
 *     Backend owns, so on web we mainly persist intent + gate the prompt.
 *
 * All functions degrade gracefully and never throw.
 */

import { isNativeApp } from '../config';
import { getSettings, setSettings, markNotifyPrompted, wasNotifyPrompted } from '../storage/local';

const DAILY_NOTIFICATION_ID = 1001;
const DEFAULT_HOUR = 19; // 7pm local — evening reminder before the day rolls over

export type NotifyPermission = 'granted' | 'denied' | 'unsupported' | 'default';

/** Current OS/browser notification permission. */
export function notifyPermission(): NotifyPermission {
  if (isNativeApp()) return 'default'; // resolved at request time by the plugin
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as NotifyPermission;
}

/** Whether we should surface the "turn on daily reminders" prompt. */
export function shouldOfferReminders(): boolean {
  if (wasNotifyPrompted()) return false;
  const perm = notifyPermission();
  return perm === 'default' || isNativeApp();
}

/**
 * Ask for permission and, if granted, schedule the daily reminder. Returns the
 * resulting permission. Marks the prompt as shown so we don't nag.
 */
export async function enableDailyReminder(hour: number = DEFAULT_HOUR): Promise<NotifyPermission> {
  markNotifyPrompted();

  if (isNativeApp()) {
    const granted = await enableNativeReminder(hour);
    setSettings({ dailyReminder: granted });
    return granted ? 'granted' : 'denied';
  }

  if (typeof Notification === 'undefined') return 'unsupported';
  let perm = Notification.permission as NotifyPermission;
  if (perm === 'default') {
    try {
      perm = (await Notification.requestPermission()) as NotifyPermission;
    } catch {
      perm = 'denied';
    }
  }
  setSettings({ dailyReminder: perm === 'granted' });
  return perm;
}

/** Turn the reminder off (cancels native schedule when applicable). */
export async function disableDailyReminder(): Promise<void> {
  setSettings({ dailyReminder: false });
  if (isNativeApp()) {
    try {
      const mod = await import('@capacitor/local-notifications');
      await mod.LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
    } catch {
      /* plugin absent — nothing to cancel */
    }
  }
}

async function enableNativeReminder(hour: number): Promise<boolean> {
  try {
    const mod = await import('@capacitor/local-notifications');
    const { LocalNotifications } = mod;
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') return false;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: DAILY_NOTIFICATION_ID,
          title: 'Your daily roll is waiting',
          body: "Keep your streak alive — today's course resets at midnight UTC.",
          schedule: { on: { hour, minute: 0 }, repeats: true, allowWhileIdle: true },
        },
      ],
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Register for REMOTE push (Capacitor). Returns the device token for Backend to
 * store and target. No-op on web (Backend owns the web-push service worker side).
 */
export async function registerPushToken(): Promise<string | null> {
  if (!isNativeApp()) return null;
  try {
    const mod = await import('@capacitor/push-notifications');
    const { PushNotifications } = mod;
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return null;

    return await new Promise<string | null>((resolve) => {
      const done = (token: string | null): void => resolve(token);
      PushNotifications.addListener('registration', (t: { value: string }) => done(t.value));
      PushNotifications.addListener('registrationError', () => done(null));
      void PushNotifications.register();
      // Safety timeout so a missing callback never hangs the caller.
      setTimeout(() => done(null), 8000);
    });
  } catch {
    return null;
  }
}

/** Whether the user currently has the reminder enabled (per settings). */
export function reminderEnabled(): boolean {
  return getSettings().dailyReminder;
}
